<template>
  <div class="statistics">
    <b-loading :active="loading" />
    <div class="mb-4 page-title">
      <h1>
        統計報表
        <small class="subtitle">此處可以依日期範圍查詢訂購報表</small>
      </h1>
    </div>
    <div class="statistics__section statistics-table">
      <section class="is-flex is-flex-wrap-wrap statistics-infos">
        <div class="is-align-items-start is-flex is-flex-direction-column mx-7 my-6">
          <label class="has-text-grey mb-2 statistics-infos--title">查詢日期範圍</label>
          <div class="is-flex items-center">
            <div class="mr-4">
              <b-radio
                v-model="dateType"
                class="mr-3"
                name="date_type"
                native-value="start"
              >
                依開放訂購日
              </b-radio>
              <b-radio
                v-model="dateType"
                name="date_type"
                native-value="receive"
              >
                依領取起始日
              </b-radio>
            </div>
            <b-field>
              <b-datepicker
                :date-formatter="dateFormatter"
                :first-day-of-week="1"
                :min-date="isChangingDate ? minDate : null"
                :max-date="maxDate"
                class="statistics-infos__date-picker"
                icon="calendar-alt"
                locale="zh-TW"
                placeholder="請選擇日期範圍"
                range
                v-model="dates"
                @range-end="onEndChanges"
                @range-start="onStartChanges"
              />
            </b-field>
          </div>
        </div>
        <template v-if="hasDateRange">
          <div class="is-align-items-center is-flex is-flex-direction-column mx-7 my-6">
            <label class="has-text-grey mb-2 statistics-infos--title">總訂購次數</label>
            <span class="is-size-5">{{ totalOrderCount }}</span>
          </div>
          <div class="is-align-items-center is-flex is-flex-direction-column mx-7 my-6">
            <label class="has-text-grey mb-2 statistics-infos--title">總訂購金額</label>
            <span class="is-size-5">{{ totalAmount }}</span>
          </div>
          <div class="is-align-items-center is-flex is-flex-direction-column mx-7 my-6">
            <label class="has-text-grey mb-2 statistics-infos--title">總訂單數</label>
            <span class="is-size-5">{{ totalOrderedCount }}</span>
          </div>
        </template>
      </section>
    </div>
    <div class="statistics__section statistics-table">
      <b-tabs v-model="activeTab" class="statistics-tabs" @input="onTabChange">
        <b-tab-item
          value="groupBuyings"
          label="依訂購統計" />
        <b-tab-item
          value="members"
          label="依人員統計" />
      </b-tabs>
      <section class="flex items-center justify-between m-4">
        <div class="flex items-start">
          <b-dropdown aria-role="list">
            <template #trigger="{ active }">
              <b-button
                :label="currentFilterLabel"
                :icon-right="active ? 'chevron-up' : 'chevron-down'" />
            </template>

            <b-dropdown-item
              v-for="filterType in filterTypes"
              aria-role="listitem"
              :key="filterType.key"
              @click="setFilterType(filterType.key)">
              {{ filterType.label }}
            </b-dropdown-item>
          </b-dropdown>
          <div v-if="filter.type !== 'tag'" class="flex flex-col ml-4">
            <b-input
              v-model="filter.value"
              :disabled="!hasDateRange"
              class="min-w-[400px]"
              placeholder="請輸入搜尋文字"
              @keypress.enter.native="onSearch"
            />
            <span v-if="filter.type === 'category'" class="text-gray-500 text-xs">
              搜尋品項類別時，僅統計該時段所有訂購中包含該類別品項之訂單
            </span>
          </div>
          <b-taginput
            v-else
            v-model="filter.tags"
            :disabled="!hasDateRange"
            class="ml-4 min-w-[400px]"
            ellipsis
            placeholder="請輸入訂購標籤"
            ref="taginput"
          >
            <template #selected="props">
              <b-tag
                v-for="(tag, index) in props.tags"
                :key="`search-tag-${tag}-${index}`"
                closable
                rounded
                type="is-primary-light"
                @close="$refs.taginput.removeTag(index, $event)"
              >
                {{ tag }}
              </b-tag>
            </template>
          </b-taginput>
          <div class="flex gap-6 ml-4">
            <w-button
              :disabled="isSubmitDisabled || !hasDateRange"
              size="sm"
              @click="onSearch">
              搜尋
            </w-button>
            <w-button
              color-type="secondary"
              size="sm"
              type="link"
              @click="onReset"
            >
              清除
            </w-button>
            <w-button
              :disabled="!hasDateRange"
              size="sm"
              type="link"
              @click="openedFilter = !openedFilter"
            >
              進階篩選
              <i
                :class="openedFilter ? 'icon-up-chevron' : 'icon-down-chevron'"
                class="ml-2"
              />
            </w-button>
          </div>
        </div>
        <div class="flex items-center justify-end">
          <w-button
            :disabled="disableExport"
            :loading="isExporting"
            color-type="secondary"
            size="func"
            type="link"
            @click="exportReport"
          >
            <span class="flex items-center">
              <i
                :class="disableExport ? 'text-gray-300' : 'text-secondary-500'"
                class="icon-cloud-download mr-2 text-lg"
              />
              匯出報表
            </span>
          </w-button>
        </div>
      </section>
      <div v-if="openedFilter" class="bg-light-gray-400 mx-4 rounded px-2 py-4">
        <div class="flex items-center">
          <label class="flex-shrink-0 mr-4 text-gray-500 text-sm">訂購狀態</label>
          <span
            v-for="item in filterTagItems"
            :class="{ active: filterTagIsActive(item) }"
            :key="`selection-filter-${item.value}`"
            :title="item.label"
            class="selection-item"
            @click="onSelectItem(item)"
          >
            {{ item.label }}
          </span>
        </div>
      </div>
      <div class="m-4">
        <div v-if="isFiltered" class="mb-2 text-xs">
          你正在找
          <span
            v-if="currentFilter.type !== 'tag'"
            class="has-text-link">
            {{ currentFilter.value }}
          </span>
          <b-tag
            v-else
            v-for="tag in currentFilter.tags"
            type="is-primary"
            class="ml-1"
            rounded
            :key="tag">
            {{ tag }}
          </b-tag>
          ，符合條件共 {{ reports.length }} 筆結果
        </div>
        <group-buyings-list
          :current-page="page"
          :current-per-page="perPage"
          :date-type="dateType"
          :has-date-range="hasDateRange"
          :orders="formattedReports"
          :loading="loading"
          :show-title="!isFiltered"
          :sort-field="sortField"
          :sort-order="sortOrder"
          @sort="onSort"
        />
      </div>
      <div v-if="total && !loading && hasDateRange" class="flex gap-4 items-center mx-4">
        <b-select v-model="perPage">
          <option
            v-for="option in perPageOptions"
            :value="option.value"
            :key="option.label"
          >
            {{ option.label }}
          </option>
        </b-select>
        <div class="flex-grow">
          <b-pagination
            :total="total"
            v-model="page"
            range-before="3"
            range-after="1"
            nextIcon="right-chevron"
            prevIcon="left-chevron"
            :per-page="perPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GroupBuyingStatistics from './GroupBuyingStatistics';

export default GroupBuyingStatistics;
</script>

<style lang="scss" scoped>
@import './GroupBuyingStatistics';
</style>
